import { SyncPhase } from '../../types/sync/enums';
import { TranslationKey } from '../../i18n/translationKeys';

// Maps a sync phase to its localized "Étape X sur 3 — …" translation key, used by the customer
// follow-up page's re-sync label.
//
// Returns null when there is no phase yet, and the caller renders no step label. The phases are
// reported by the backend over SSE, so no phase is known until the payload has been posted and the
// first snapshot arrives — on the Acomba path that window covers the whole bridge extraction, the
// longest part of a Full sync. Defaulting it to phase 1 would claim "Step 1 of 3 — Preparing data…"
// while the backend had received nothing at all.
// Backend phase order (SyncSaga): Preprocessing → PriorityRanking → RelanceFill. A veil or checklist
// that must stay up "until RelanceFill" compares positions in this list rather than the enum's string
// values, which carry no ordering of their own.
const SYNC_PHASE_ORDER: SyncPhase[] = [
    SyncPhase.Preprocessing,
    SyncPhase.PriorityRanking,
    SyncPhase.RelanceFill,
];

/**
 * True while `phase` has not yet reached `target` — including when `phase` is null/undefined, which
 * means no snapshot has arrived yet and is therefore before every phase. Used to gate anything that
 * must hold until a later phase (Home's checklist, a follow-up page's sync veil) without hard-coding
 * the phase list at each call site.
 */
export function isPhaseBefore(phase: SyncPhase | null | undefined, target: SyncPhase): boolean {
    if (!phase) return true;
    return SYNC_PHASE_ORDER.indexOf(phase) < SYNC_PHASE_ORDER.indexOf(target);
}

/**
 * True once `phase` has reached or passed `target` — the complement of {@link isPhaseBefore}, and
 * null/undefined (no snapshot yet) is therefore always false. Used to latch something on once a phase
 * has been reached (`useFillReached`, the checklist's per-row "done").
 */
export function isPhaseAtOrAfter(phase: SyncPhase | null | undefined, target: SyncPhase): boolean {
    return !isPhaseBefore(phase, target);
}

export function getSyncPhaseTranslationKey(phase: SyncPhase | null | undefined): string | null {
    switch (phase) {
        case SyncPhase.PriorityRanking:
            return TranslationKey.Settings.SyncPhasePriorityRanking;
        case SyncPhase.RelanceFill:
            return TranslationKey.Settings.SyncPhaseRelanceFill;
        case SyncPhase.Preprocessing:
            return TranslationKey.Settings.SyncPhasePreprocessing;
        default:
            return null;
    }
}
